import * as React from 'react'
import { Button } from '../../../../components/UI'
import { useTranslation } from '../../../../lib/context/i18n'
import { isServer } from '../../../../lib/utils'
import { getLicenseKey } from '../../../../lib/utils/api'
import { Styled } from './LicenseItem.styled'
import { License } from './types'
import useEventTracking from '../../../../lib/hooks/tracking'

const useLicenseKey = (license: License) => {
  const [key, setKey] = React.useState<string | undefined>()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | undefined>()
  const trackEvent = useEventTracking();

  React.useEffect(() => {
    setLoading(true)
    setError(undefined)
    setKey(undefined)

    // escape / in query param
    const id = license.id.replace('/', '%2F')

    getLicenseKey(id)
      .then(data => {
        setKey(data.key)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [license])

  const copyToClipboard = React.useCallback(() => {
    if (isServer()) return

    if (key) {
      trackEvent('license_key_loaded')
      navigator.clipboard.writeText(key)
    }
  }, [key])

  return {
    key,
    loading,
    error,
    copyToClipboard,
  }
}

export const LicenseItem = ({ license }: { license: License }) => {
  const { error, key, loading, copyToClipboard } = useLicenseKey(license)
  const t = useTranslation()

  return (
    <Styled.Wrapper>
      {loading && <div>Loading...</div>}
      {error && <div>Error: {error}</div>}
      {key && (
        <Styled.KeyWrapper>
          <Styled.Key>{key}</Styled.Key>
          <Button $variant="primary" $inverted iconRight="copy-document" $size="xsmall" onClick={copyToClipboard}>
            {t('COPY_LICENSE')}
          </Button>
        </Styled.KeyWrapper>
      )}
    </Styled.Wrapper>
  )
}
